<template>
  <div class="row">
    <!-- Left Column: Titles + Description -->
    <div class="col-md-8">
      <!-- Titles -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="fas fa-heading mr-2"></i>Titles</h5>
        </div>
        <div class="card-body">
          <div v-for="(content, lang) in offer.contents" :key="'title-' + lang" class="mb-2">
            <!-- Edit mode: title only. Nothing else on this page is editable. -->
            <div v-if="editingLang === lang" class="d-flex align-items-center">
              <b-badge variant="light" class="mr-2 text-uppercase">{{ lang }}</b-badge>
              <b-form-input
                v-model="draftTitle"
                size="sm"
                class="mr-2"
                :disabled="isSavingTitle"
                @keyup.enter="saveTitle(lang)"
              />
              <b-button
                size="sm"
                variant="primary"
                class="mr-1"
                :disabled="isSavingTitle"
                @click="saveTitle(lang)"
              >
                Save
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="isSavingTitle"
                @click="cancelTitleEdit"
              >
                Cancel
              </b-button>
            </div>
            <div v-else class="d-flex align-items-start">
              <b-badge variant="light" class="mr-2 text-uppercase">{{ lang }}</b-badge>
              <span>{{ titleOf(lang, content) }}</span>
              <CopyButton v-if="titleOf(lang, content)" :text="titleOf(lang, content)" />
              <b-button
                size="sm"
                variant="link"
                class="py-0"
                title="Edit title"
                @click="startTitleEdit(lang, content)"
              >
                <i class="fas fa-pen"></i>
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="fas fa-file-alt mr-2"></i>Description</h5>
        </div>
        <div class="card-body">
          <b-tabs v-if="hasMultipleLanguages" content-class="mt-3">
            <b-tab v-for="(content, lang) in offer.contents" :key="'desc-' + lang" :title="lang.toUpperCase()">
              <div class="d-flex justify-content-end mb-2">
                <CopyButton v-if="content.description" :text="stripHtml(content.description)" />
                <small class="text-muted ml-1">Copy as text</small>
              </div>
              <div v-html="content.description" class="offer-description" />
            </b-tab>
          </b-tabs>
          <div v-else-if="primaryContent">
            <div class="d-flex justify-content-end mb-2">
              <CopyButton v-if="primaryContent.description" :text="stripHtml(primaryContent.description)" />
              <small class="text-muted ml-1">Copy as text</small>
            </div>
            <div v-html="primaryContent.description" class="offer-description" />
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar: All compact info -->
    <div class="col-md-4">
      <!-- Company -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="fas fa-building mr-2"></i>Company</h5>
        </div>
        <div class="card-body">
          <div v-if="offer.company">
            <div class="d-flex align-items-center">
              <router-link
                :to="{ name: 'company', params: { companyId: offer.company.id } }"
                class="font-weight-bold"
              >
                {{ offer.company.name_fr }}
              </router-link>
              <CopyButton :text="offer.company.name_fr" />
            </div>
            <div v-if="offer.company.name_en && offer.company.name_en !== offer.company.name_fr" class="text-muted small d-flex align-items-center">
              {{ offer.company.name_en }}
              <CopyButton :text="offer.company.name_en" />
            </div>
          </div>
          <span v-else class="text-muted">No company</span>
        </div>
      </div>

      <!-- Quick Info (single card, multiple sections) -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="fas fa-briefcase mr-2"></i>Job Info</h5>
        </div>
        <div class="card-body p-0">
          <!-- Location -->
          <div class="info-row">
            <strong class="small text-muted"><i class="fas fa-map-marker-alt mr-1"></i>Location</strong>
            <div>
              <span v-if="offerLocation">{{ offerLocation }}</span>
              <span v-else class="text-muted">-</span>
              <b-badge :variant="offer.remote ? 'success' : 'secondary'" class="ml-1">
                {{ offer.remote ? 'Remote' : 'On-site' }}
              </b-badge>
            </div>
          </div>

          <!-- Salary -->
          <div class="info-row">
            <strong class="small text-muted"><i class="fas fa-dollar-sign mr-1"></i>Salary</strong>
            <div v-if="offer.salary_min || offer.salary_max">
              <template v-if="offer.salary_min">{{ offer.salary_min }}</template>
              <template v-if="offer.salary_min && offer.salary_max"> - </template>
              <template v-if="offer.salary_max">{{ offer.salary_max }}</template>
              <template v-if="offer.salary_type"> ({{ offer.salary_type }})</template>
            </div>
            <span v-else class="text-muted">-</span>
          </div>

          <!-- Language -->
          <div class="info-row">
            <strong class="small text-muted"><i class="fas fa-language mr-1"></i>Primary Language</strong>
            <div>{{ offer.primary_language ? offer.primary_language.toUpperCase() : '-' }}</div>
          </div>

          <!-- Dates -->
          <div class="info-row">
            <strong class="small text-muted"><i class="fas fa-calendar mr-1"></i>Created</strong>
            <div>{{ formatDate(offer.creation_date) }}</div>
          </div>
          <div class="info-row">
            <strong class="small text-muted"><i class="fas fa-calendar-times mr-1"></i>Expires</strong>
            <div>{{ formatDate(offer.expiration_date) }}</div>
          </div>

          <!-- Internal Ref -->
          <div v-if="offer.internal_reference_number" class="info-row">
            <strong class="small text-muted"><i class="fas fa-hashtag mr-1"></i>Internal Ref</strong>
            <div>{{ offer.internal_reference_number }}</div>
          </div>

          <!-- Status -->
          <div class="info-row">
            <strong class="small text-muted"><i class="fas fa-toggle-on mr-1"></i>Status</strong>
            <div><b-badge :variant="statusVariant">{{ offer.status }}</b-badge></div>
          </div>

          <!-- Feed In -->
          <div v-if="offer.feed_in" class="info-row">
            <strong class="small text-muted"><i class="fas fa-rss mr-1"></i>Feed In</strong>
            <div><b-badge variant="info">{{ offer.feed_in.legacy_client }}</b-badge></div>
          </div>
        </div>
      </div>

      <!-- Attributes -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="fas fa-tags mr-2"></i>Attributes</h5>
        </div>
        <div class="card-body">
          <div v-if="offer.offer_attributes && offer.offer_attributes.length">
            <div v-for="(attrs, type) in groupedAttributes" :key="type" class="mb-2">
              <strong class="small text-muted text-capitalize">{{ type }}:</strong>
              <div class="mt-1">
                <b-badge
                  v-for="attr in attrs"
                  :key="attr.id"
                  :variant="getAttributeVariant(type)"
                  class="mr-1 mb-1"
                >
                  {{ attr.label_fr || attr.label_en || attr.label }}
                </b-badge>
              </div>
            </div>
          </div>
          <div v-else class="text-muted small">No attributes assigned</div>
        </div>
      </div>

      <!-- Links -->
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="fas fa-link mr-2"></i>Links</h5>
        </div>
        <div class="card-body">
          <div v-if="jobboardUrl" class="mb-2">
            <strong class="small text-muted">Jobboard URL</strong>
            <div class="d-flex align-items-center">
              <a :href="jobboardUrl" target="_blank" class="small text-truncate d-block" style="max-width: 85%;" :title="jobboardUrl">
                {{ jobboardUrl }}
              </a>
              <CopyButton :text="jobboardUrl" />
            </div>
          </div>
          <div v-if="applyExternalLink" class="mb-2">
            <strong class="small text-muted">Apply External Link</strong>
            <div class="d-flex align-items-center">
              <a :href="applyExternalLink" target="_blank" class="small text-truncate d-block" style="max-width: 85%;" :title="applyExternalLink">
                {{ applyExternalLink }}
              </a>
              <CopyButton :text="applyExternalLink" />
            </div>
          </div>
          <div v-if="!jobboardUrl && !applyExternalLink" class="text-muted small">No links available</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BButton, BFormInput, BTabs, BTab } from "bootstrap-vue";
import CopyButton from "@/components/CopyButton.vue";

export default {
  components: { BBadge, BButton, BFormInput, BTabs, BTab, CopyButton },
  props: {
    offer: { type: Object, required: true },
    offerId: { type: [String, Number], required: true }
  },
  data() {
    return {
      editingLang: null,
      draftTitle: "",
      isSavingTitle: false,
      // Titles saved during this visit, so the display is right even before
      // the parent refetches the offer.
      savedTitles: {}
    };
  },
  computed: {
    primaryContent() {
      if (!this.offer.contents) return null;
      const lang = this.offer.primary_language || "fr";
      return this.offer.contents[lang] || Object.values(this.offer.contents)[0] || null;
    },
    hasMultipleLanguages() {
      return this.offer.contents && Object.keys(this.offer.contents).length > 1;
    },
    statusVariant() {
      const v = { published: "success", draft: "secondary", expired: "danger", pending: "warning" };
      return v[this.offer.status] || "secondary";
    },
    groupedAttributes() {
      if (!this.offer.offer_attributes) return {};
      return this.offer.offer_attributes.reduce((acc, attr) => {
        const type = attr.type || "other";
        if (!acc[type]) acc[type] = [];
        acc[type].push(attr);
        return acc;
      }, {});
    },
    offerLocation() {
      if (!this.offer.location) return null;
      const loc = typeof this.offer.location === "string" ? JSON.parse(this.offer.location) : this.offer.location;
      const parts = [loc.city, loc.state, loc.country].filter(Boolean);
      return parts.length ? parts.join(", ") : null;
    },
    jobboardUrl() {
      if (!this.offer.jobboards || !this.offer.jobboards.length) return null;
      const content = this.primaryContent;
      if (!content) return null;
      const published = this.offer.jobboards
        .filter(jb => jb.pivot && jb.pivot.status === "published" && jb.name !== "career" && jb.name !== "hotelsjobs")
        .sort((a, b) => {
          const priority = (jb) => {
            if (jb.is_specialized && !jb.is_jobsmedia_io) return 0;
            if (jb.is_specialized && jb.is_jobsmedia_io) return 1;
            if (!jb.is_specialized && jb.is_jobsmedia_io) return 2;
            return 3;
          };
          return priority(a) - priority(b);
        });
      if (!published.length) return null;
      const best = published[0];
      const lang = content.lang || this.offer.primary_language || "fr";
      const path = lang === "fr" ? "offre" : "offer";
      const domain = best.url ? best.url.replace(/^https?:\/\//, "").replace(/\/$/, "") : best.label;
      return `https://${domain}/${lang}/${path}/${content.slug}/${this.offer.id}`;
    },
    applyExternalLink() {
      const content = this.primaryContent;
      return content?.apply_external_link || null;
    }
  },
  methods: {
    titleOf(lang, content) {
      return lang in this.savedTitles ? this.savedTitles[lang] : content.title;
    },
    startTitleEdit(lang, content) {
      this.editingLang = lang;
      this.draftTitle = this.titleOf(lang, content) || "";
    },
    cancelTitleEdit() {
      this.editingLang = null;
      this.draftTitle = "";
    },
    async saveTitle(lang) {
      const title = (this.draftTitle || "").trim();
      if (!title) {
        this.$bvToast.toast("The title cannot be empty", { title: "Error", variant: "danger", solid: true });
        return;
      }
      this.isSavingTitle = true;
      try {
        const { data } = await this.$http.put(`/enroll_admin/offer/${this.offerId}/title`, { lang, title });
        this.$set(this.savedTitles, lang, data.title);
        this.editingLang = null;
        this.$bvToast.toast("Title updated", { title: "Success", variant: "success", solid: true });
        // Let the parent refetch: the page header shows the same title.
        this.$emit("offer-updated");
      } catch (error) {
        // Lumen returns a flat map of field errors on 422 ({"title":["…"]}),
        // measured; anything else falls back to a generic message.
        const validation = error.response?.data;
        const message = validation?.title?.[0] || validation?.lang?.[0] || "Failed to update the title";
        this.$bvToast.toast(message, { title: "Error", variant: "danger", solid: true });
      } finally {
        this.isSavingTitle = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString();
    },
    getAttributeVariant(type) {
      const v = { category: "primary", specialization: "info", experience: "warning" };
      return v[type] || "secondary";
    },
    stripHtml(html) {
      if (!html) return "";
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent || "";
    }
  }
};
</script>

<style scoped>
.card-title { font-size: 1rem; }
.offer-description { line-height: 1.6; }
.offer-description :deep(ul),
.offer-description :deep(ol) { padding-left: 20px; }
.offer-description :deep(p) { margin-bottom: 1rem; }
.info-row {
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.info-row:last-child { border-bottom: none; }
</style>
